import React from "react";
import { X, Loader2, CheckCircle2 } from "lucide-react";
import { toUnicodeUrl } from "../utils/url.js";
import { SOCIAL_NETWORKS } from "../constants/socialNetworks.js";

export default function ShareModal({
  onClose,
  shareUrl,
  idea = "Бизнес-план",
  isActive,
  activatedAt,
  onActivate,
  activating,
}) {
  const shareText = `Мой бизнес план: ${idea || "Бизнес-план"}`;
  const formattedDate = activatedAt ? new Date(activatedAt).toLocaleString("ru-RU") : null;
  const [copied, setCopied] = React.useState(false);
  const displayUrl = React.useMemo(() => toUnicodeUrl(shareUrl), [shareUrl]);

  const shareLink = displayUrl;

  const onCopy = async () => {
    if (!displayUrl) return;
    try {
      await navigator.clipboard.writeText(displayUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      // noop
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 px-4 py-6">
      <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
          aria-label="Закрыть"
        >
          <X className="h-5 w-5" />
        </button>
        <h2 className="text-xl font-semibold text-slate-900">Поделиться бизнес-планом</h2>
        <p className="mt-3 text-sm leading-relaxed text-slate-600">
          Ваше имя, почта, и любые данные (кроме идеи, описания, города, собственных средств), которые вы добавляете для бизнес
          плана, остаются конфиденциальными.
        </p>
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500">Ссылка</div>
          <a
            href={isActive ? shareLink : undefined}
            className={`mt-1 block break-all font-medium ${isActive ? "text-blue-600 hover:text-blue-700" : "text-slate-400"}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {displayUrl}
          </a>
          {isActive ? (
            <div className="mt-2 flex items-center gap-3 text-xs text-emerald-600">
              <div className="flex items-center">
                <CheckCircle2 className="mr-1 h-4 w-4" />
                {formattedDate ? `Активирована ${formattedDate}` : "Ссылка активирована"}
              </div>
              <button
                type="button"
                onClick={onCopy}
                className="inline-flex items-center rounded border border-emerald-300 px-2 py-1 text-emerald-700 hover:bg-emerald-50"
              >
                {copied ? "Скопировано" : "копировать"}
              </button>
            </div>
          ) : (
            <div className="mt-2 text-xs text-slate-500">Ссылка не активирована</div>
          )}
        </div>
        <div className="mt-6 grid grid-cols-3 gap-3">
          {SOCIAL_NETWORKS.map((item) => {
            const href = shareLink ? item.buildHref(shareLink, shareText) : "";
            return (
              <a
                key={item.id}
                href={isActive ? href : undefined}
                target="_blank"
                rel="noopener noreferrer"
                aria-disabled={!isActive}
                className={`flex flex-col items-center justify-center rounded-xl border border-slate-200 p-3 text-center text-sm font-medium transition ${
                  isActive
                    ? "bg-white text-slate-700 hover:border-transparent hover:shadow"
                    : "bg-slate-50 text-slate-400"
                }`}
              >
                <span
                  className={`mb-2 flex h-12 w-12 items-center justify-center rounded-full text-white ${item.color} ${
                    isActive ? "" : "opacity-60"
                  }`}
                >
                  {item.icon}
                </span>
                {item.name}
              </a>
            );
          })}
        </div>
        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xs text-slate-500">
            {isActive ? "Ссылка готова к отправке" : "Активируйте ссылку, чтобы поделиться планом"}
          </div>
          {isActive ? (
            <button
              type="button"
              onClick={onActivate}
              className="text-sm font-medium text-slate-600 underline underline-offset-4 hover:text-slate-800"
            >
              Отменить
            </button>
          ) : (
            <button
              type="button"
              onClick={onActivate}
              disabled={activating}
              className={`inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition ${
                activating ? "bg-slate-400" : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {activating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Активировать"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
